import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";

import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity, Locatable} from "./entity";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {GeofenceType, toGeofenceType} from "./geofence-type";
import {GeofenceTriggerType, toGeofenceTriggerType} from "./geofence-trigger-type";
import {GeoCoord} from "./geo-coord";

export const GeofenceCodec = t.intersection([
  t.type({
    id: t.string,
    objectId: t.string,
    objectType: t.string,
    type: t.string,
    triggerType: t.string,
    latCoord: t.number,
    longCoord: t.number,
    radius: t.number,
  }),
  t.partial({
    boundary: t.any,
    description: t.string,
    regionIds: t.array(t.string),
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const GeofenceArrayCodec = t.array(GeofenceCodec);

export type Geofence = t.TypeOf<typeof GeofenceCodec>;
export type GeofenceArray = t.TypeOf<typeof GeofenceArrayCodec>;

@Exclude()
export class GeofenceEntity implements Entity, Locatable {
  @Expose()
  id!: string;

  @Expose()
  objectId!: string;

  @Expose()
  objectType!: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toGeofenceType))
  type!: GeofenceType;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toGeofenceTriggerType))
  triggerType!: GeofenceTriggerType;

  @Expose()
  description?: string;

  @Expose()
  latCoord!: number;

  @Expose()
  longCoord!: number;

  @Expose()
  radius!: number;

  @Expose()
  boundary?: string;

  @Expose()
  regionIds?: string[];

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = 'GEOFENCE';

  get displayName(): Readonly<string> {
    return this.id;
  }

  get latLng(): Readonly<GeoCoord> {
    return {
      latCoord: this.latCoord,
      longCoord: this.longCoord,
    };
  }

  /**
   * Constructor for GeofenceEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: Geofence): GeofenceEntity {
    return plainToInstance(GeofenceEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<Geofence> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(GeofenceCodec, object);
  }

  asTypeValue(): Geofence {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(GeofenceCodec, object);
  }
}
